import * as React from 'react';
import {StatefulComponent, inject, deps, observer} from '../../lib/component';
import {colorBlack, colorBrand, colorWhite} from '../../theme/color';
import {classes, style} from 'typestyle';
import {em, important, rem} from 'csx';
import {graphql, InjectedGraphQLProps} from 'react-apollo';
import gql from 'graphql-tag';
import maybe from '../../lib/maybe';
import {content, horizontal, horizontallySpaced, start, startJustified, verticallySpaced, wrap} from 'csstips';
import {mediaTablet} from '../../theme/media';

export type HeroSearchInputSuggestion = {
  name: string;
  slug: string;
  images: {
    identity: string;
  }
}

export type HeroSearchInputSuggestionsProps = {
  query: string;
  activeIndex: number;
  show?: boolean;
  onSuggestions(matches: HeroSearchInputSuggestion[]): any;
  onHighlight(matches: HeroSearchInputSuggestion[], index: number): any;
  onSelect(matches: HeroSearchInputSuggestion[], index: number): any;
}

type State = {

}

export type HeroSearchInputSuggestionDataType = {
  find: null | {
    matches: null | {
      results: null | HeroSearchInputSuggestion[]
    }
  }
};

type DataProps = {
  loading: boolean;
  error?: any;
}

type MaybeData = HeroSearchInputSuggestionDataType & DataProps | null;
type CombinedProps = HeroSearchInputSuggestionsProps & InjectedGraphQLProps<HeroSearchInputSuggestionDataType>;

@inject(deps) @observer
export default class HeroSearchInputSuggestions extends StatefulComponent<CombinedProps, State> {
  private $suggestions?: HTMLDivElement;

  render() {
    const {show, activeIndex, data, onHighlight, onSelect} = this.props;

    const matches = maybe<HeroSearchInputSuggestion[]>(
      () => data!.find!.matches!.results!,
      []
    );

    const className = classes(
      HeroSearchInputSuggestions.styles.container,
      show && matches.length > 0
        ? HeroSearchInputSuggestions.styles.containerVisible
        : HeroSearchInputSuggestions.styles.containerHidden
    );

    return (
      <div ref={el => this.$suggestions = el} className={className}>
        {show && matches.map(({name}, index) => (
          <div
            className={classes(
              HeroSearchInputSuggestions.styles.suggestion,
              activeIndex === index && HeroSearchInputSuggestions.styles.suggestionHighlighted
            )}
            onMouseOut={onHighlight.bind(null, matches, -1)}
            onMouseDown={onSelect.bind(null, matches, index)}
            onMouseOver={onHighlight.bind(null, matches, index)}
            data-index={index}
            key={index + name}
            children={name}
          />
        ))}
      </div>
    )
  }

  componentDidUpdate(props: CombinedProps, state) {
    const oldMatches = maybe<HeroSearchInputSuggestion[]>(
      () => props.data!.find!.matches!.results!,
      []
    );

    const newMatches = maybe<HeroSearchInputSuggestion[]>(
      () => this.props.data!.find!.matches!.results!,
      []
    );

    if (props.activeIndex !== this.props.activeIndex && this.props.activeIndex >= 0) {
      this.props.onHighlight(newMatches, newMatches[this.props.activeIndex] ? this.props.activeIndex : newMatches.length - 1);
    }

    if (oldMatches !== newMatches) {
      this.props.onSuggestions(newMatches);
    }
  }

  static styles = {
    container: style({
      padding: '8px 0 0 4px',
      overflowX: 'hidden',
      overflowY: 'auto',
      transition: 'opacity 0.125s ease-in-out',
      height: rem(6),
    }),
    containerHidden: style({
      opacity: 0,
    }),
    containerVisible: style({
      opacity: 1,
    }),
    suggestion: style(
      {
        display: 'inline-block',
        borderRadius: em(1.6),
        lineHeight: em(1.2),
        padding: '0.6em 1.4em',
        margin: '0 4px 4px 0',
        cursor: 'pointer',
        fontSize: rem(0.75),
        fontWeight: 500,
        whiteSpace: 'pre',
        textOverflow: 'ellipsis',
        color: colorWhite.toString(),
        background: colorBlack.fade(0.3).toString(),
      },
      mediaTablet({
        fontSize: rem(1)
      })
    ),
    suggestionHighlighted: style({
      color: important(colorWhite.toString()),
      background: important(colorBrand.toString()),
    })
  }
}

export const ArtistHeroSearchInputSuggestions = graphql(gql`
  query ($query: String!) {
    find(query: $query) {
      matches: artists(pagination: {size: 12, from: 0}) {
        results {
          name
          slug
          images {
            identity
          }
        }
      }
    }
  }
`, {
  options: (variables: HeroSearchInputSuggestionsProps) => ({
    variables,
  })
})(HeroSearchInputSuggestions);

export const ThemeHeroSearchInputSuggestions = graphql(gql`
  query ($query: String!) {
    find(query: $query) {
      matches: themes(pagination: {size: 24, from: 0}) {
        results {
          name
          slug
          images {
            identity
          }
        }
      }
    }
  }
`, {
  options: (variables: HeroSearchInputSuggestionsProps) => ({
    variables,
  })
})(HeroSearchInputSuggestions);

export const GenreHeroSearchInputSuggestions = graphql(gql`
  query ($query: String!) {
    find(query: $query) {
      matches: genres(pagination: {size: 24, from: 0}) {
        results {
          name
          slug
          images {
            identity
          }
        }
      }
    }
  }
`, {
  options: (variables: HeroSearchInputSuggestionsProps) => ({
    variables,
  })
})(HeroSearchInputSuggestions);

export const MoodHeroSearchInputSuggestions = graphql(gql`
  query ($query: String!) {
    find(query: $query) {
      matches: moods(pagination: {size: 24, from: 0}) {
        results {
          name
          slug
          images {
            identity
          }
        }
      }
    }
  }
`, {
  options: (variables: HeroSearchInputSuggestionsProps) => ({
    variables,
  })
})(HeroSearchInputSuggestions);